import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { wallApi, storageApi } from "@/services/api";
import { STORAGE_CONFIG } from "@/config/constants";
import { extractStoragePathFromUrl, parsePhotoSetRequirement } from "@/lib/utils";
import { Upload, X, Loader2, Trophy, Link2 } from "lucide-react";

interface WallPublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  claim: { id: string; proof_url: string | null } | null;
  bounty: {
    title: string;
    description?: string | null;
    country?: string | null;
    location?: string | null;
    proof_requirements?: unknown[] | null;
  } | null;
  onPublished?: (slug: string) => void;
}

interface WallPhoto {
  key: string;
  sourcePath: string | null; // raw path in proofs bucket (null when already public)
  previewUrl: string; // preview for the dialog
  url: string; // final url to publish (filled after copy)
  caption: string;
}

let photoKeyCounter = 0;

export function WallPublishDialog({
  open,
  onOpenChange,
  claim,
  bounty,
  onPublished,
}: WallPublishDialogProps) {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [photos, setPhotos] = useState<WallPhoto[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Prefill whenever the dialog opens with a new claim
  useEffect(() => {
    if (!open) return;
    if (bounty) {
      setTitle(bounty.title || "");
      setDescription(bounty.description || "");
      setCity(bounty.location || "");
      setCountry(bounty.country || "");
    }

    // Resolve proof files into editable photos (public URLs stay, storage paths get signed previews)
    const parts = (claim?.proof_url || "").split("||").map((p) => p.trim()).filter(Boolean);
    setPhotos([]);
    setLoadingPhotos(true);

    const photoSet = parsePhotoSetRequirement(bounty?.proof_requirements);
    const defaultCaptions = photoSet?.labels || [];

    (async () => {
      const resolved: WallPhoto[] = [];
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        // Non-http entries are raw paths in the proofs bucket
        if (!part.startsWith("http")) {
          const { data, error } = await storageApi.getSignedProofUrl(part);
          resolved.push({
            key: `p${++photoKeyCounter}`,
            sourcePath: part,
            previewUrl: !error && data ? data : part,
            url: "",
            caption: defaultCaptions[i] || "",
          });
        } else if (part.includes(`/${STORAGE_CONFIG.PROOFS_BUCKET}/`)) {
          // Signed/public URL that points back into the proofs bucket
          const path = extractStoragePathFromUrl(part, STORAGE_CONFIG.PROOFS_BUCKET);
          if (path) {
            const { data, error } = await storageApi.getSignedProofUrl(path);
            resolved.push({
              key: `p${++photoKeyCounter}`,
              sourcePath: path,
              previewUrl: !error && data ? data : part,
              url: "",
              caption: defaultCaptions[i] || "",
            });
          } else {
            resolved.push({
              key: `p${++photoKeyCounter}`,
              sourcePath: null,
              previewUrl: part,
              url: part,
              caption: defaultCaptions[i] || "",
            });
          }
        } else {
          // Already a public URL (e.g. public bucket) - publish as-is
          resolved.push({
            key: `p${++photoKeyCounter}`,
            sourcePath: null,
            previewUrl: part,
            url: part,
            caption: defaultCaptions[i] || "",
          });
        }
      }
      setPhotos(resolved);
      setLoadingPhotos(false);
    })();
  }, [open, claim?.id]);

  const removePhoto = (key: string) => {
    setPhotos((prev) => prev.filter((p) => p.key !== key));
  };

  const updateCaption = (key: string, caption: string) => {
    setPhotos((prev) => prev.map((p) => (p.key === key ? { ...p, caption } : p)));
  };

  const publish = async () => {
    if (!user) {
      toast.error("You must be signed in to publish");
      return;
    }
    if (!claim) return;
    if (!title.trim()) {
      toast.error("Title is required for the wall entry");
      return;
    }
    if (photos.length === 0) {
      toast.error("At least one photo is required");
      return;
    }

    setPublishing(true);
    try {
      // Copy private proof files into the public wall-images bucket
      const publishPhotos: { url: string; caption: string }[] = [];
      for (const photo of photos) {
        let url = photo.url;
        if (!url && photo.sourcePath) {
          const { data: blob, error: downloadError } = await supabase.storage
            .from(STORAGE_CONFIG.PROOFS_BUCKET)
            .download(photo.sourcePath);
          if (downloadError || !blob) {
            throw new Error(`Could not copy proof file ${photo.sourcePath}`);
          }
          const fileName = photo.sourcePath.split("/").pop() || `photo-${Date.now()}.jpg`;
          const wallPath = `${user.id}/${Date.now()}-${fileName}`;
          const { error: uploadError } = await supabase.storage
            .from(STORAGE_CONFIG.WALL_BUCKET)
            .upload(wallPath, blob, { contentType: blob.type || "image/jpeg", upsert: false });
          if (uploadError) {
            throw new Error(`Could not publish photo: ${uploadError.message}`);
          }
          const { data: publicData } = supabase.storage
            .from(STORAGE_CONFIG.WALL_BUCKET)
            .getPublicUrl(wallPath);
          url = publicData?.publicUrl || "";
          if (!url) throw new Error("Could not generate public photo URL");
        }
        publishPhotos.push({ url, caption: photo.caption.trim() });
      }

      const { data, error } = await wallApi.publishWallEntry({
        claimId: claim.id,
        title: title.trim(),
        description: description.trim() || null,
        photos: publishPhotos,
        city: city.trim() || null,
        country: country.trim() || null,
      });

      if (error || !data) {
        throw error || new Error("Failed to publish wall entry");
      }

      toast.success("Published to the Bounty Wall!", {
        description: "Your entry is now live for everyone to see.",
      });
      onOpenChange(false);
      onPublished?.(data.slug);
    } catch (err) {
      console.error("[WallPublishDialog] Publish failed:", err);
      toast.error(err instanceof Error ? err.message : "Failed to publish wall entry");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!publishing) onOpenChange(next); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-[hsl(35_75%_42%)]" />
            Publish to the Bounty Wall
          </DialogTitle>
          <DialogDescription>
            This accepted proof will be publicly showcased with hunter attribution. You can edit everything below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="wall-title">Title</Label>
            <Input
              id="wall-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="e.g. Best tacos in Tokyo"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="wall-description">Description (optional)</Label>
            <Textarea
              id="wall-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder="Tell visitors what makes this place special..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="wall-city">City (optional)</Label>
              <Input
                id="wall-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                maxLength={80}
                placeholder="e.g. Tokyo"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wall-country">Country (optional)</Label>
              <Input
                id="wall-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                maxLength={2}
                placeholder="e.g. JP"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Photos ({photos.length})</Label>
            {loadingPhotos ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading proof photos...
              </div>
            ) : photos.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                No photos found in this proof. Photo entries need at least one image.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {photos.map((photo, index) => (
                  <div key={photo.key} className="p-3 rounded-lg border-2 border-[hsl(var(--paper-edge))] bg-background/50 space-y-2">
                    <div className="relative group aspect-video rounded-lg overflow-hidden border border-[hsl(var(--paper-edge))]">
                      <img
                        src={photo.previewUrl}
                        alt={`Wall photo ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(photo.key)}
                        className="absolute top-1.5 right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove photo"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                      <span className="absolute bottom-1.5 left-1.5 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full font-medium">
                        {index + 1}
                      </span>
                    </div>
                    <Input
                      value={photo.caption}
                      onChange={(e) => updateCaption(photo.key, e.target.value)}
                      maxLength={120}
                      placeholder="Caption (optional)"
                      className="h-8 text-xs"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {photos.length > 0 && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Link2 className="h-3 w-3" />
              Published photos become public and searchable (SEO-friendly).
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={publishing}
          >
            Skip
          </Button>
          <Button
            onClick={publish}
            disabled={publishing || loadingPhotos || photos.length === 0}
            className="bg-[hsl(35_75%_42%)] hover:bg-[hsl(35_75%_35%)]"
          >
            {publishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Publish to the Bounty Wall
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
